'use strict';

import PWABootstrap from '../pwa/PWABootstrap';

/**
 * ServiceWorkerUpdateController - Manages deferred SW updates during exam sessions.
 *
 * When a new service worker is detected during an exam:
 * 1. PWABootstrap defers the update (SKIP_WAITING not sent)
 * 2. This service tracks that a deferred update exists
 * 3. When exam ends, this service applies the deferred update
 *
 * This prevents mid-exam service worker replacement while ensuring
 * updates are applied at safe transition points.
 */
export class ServiceWorkerUpdateController {
    constructor() {
    }

    /**
     * Records telemetry without ever throwing into the update flow.
     *
     * Resolved off the global injector rather than by DI, because this service is
     * constructed without arguments and is used from route handlers that must not
     * gain a dependency on telemetry.
     */
    _recordTelemetry(eventType, severity, data) {
        try {
            const injector = window.angular?.element(document.body)?.injector();
            if (injector && injector.has('TelemetryBeaconService')) {
                injector.get('TelemetryBeaconService').record(eventType, severity, data);
            }
        } catch (e) {
            // Telemetry must never break a service worker update.
        }
    }

    /**
     * Mark that an exam is in progress.
     * Called when user successfully logs in and enters the exam.
     * This flag prevents PWABootstrap from sending SKIP_WAITING to waiting workers.
     */
    markExamInProgress() {
        console.log('ServiceWorkerUpdateController: ========== EXAM STARTED - SW updates will be deferred ==========');
        sessionStorage.setItem('examInProgress', 'true');
    }

    /**
     * Mark that exam has ended.
     * Called when navigating away from exam (to /pwa-start, logout, etc.)
     */
    markExamEnded() {
        console.log('ServiceWorkerUpdateController: Exam ended, clearing examInProgress flag');
        sessionStorage.removeItem('examInProgress');
    }

    /**
     * Check if there's a deferred update and apply it.
     * Called when navigating to /pwa-start or after exam submission.
     * @returns {Promise<boolean>} - True if an update was applied (page will reload)
     */
    async applyDeferredUpdate() {
        const hasDeferredUpdate = sessionStorage.getItem('deferredSwUpdate') === 'true';

        if (!hasDeferredUpdate) {
            console.log('ServiceWorkerUpdateController: No deferred update to apply');
            return false;
        }

        console.log('ServiceWorkerUpdateController: ========== APPLYING DEFERRED UPDATE ==========');

        // A service worker swap replaces the code running the exam, so it is the
        // first thing to check when behaviour changes mid-session. Fires at most once
        // per update, and telemetry rows already carry app_version and swBuild to
        // compare against.
        this._recordTelemetry('sw.update_applied', 'info', {
            examInProgress: sessionStorage.getItem('examInProgress') === 'true',
        });

        try {
            const registration = PWABootstrap.getRegistration();
            if (registration?.waiting) {
                console.log('ServiceWorkerUpdateController: Sending SKIP_WAITING to waiting worker');
                registration.waiting.postMessage({ type: 'SKIP_WAITING' });

                // Wait for controller change with timeout
                const controllerChanged = await new Promise((resolve) => {
                    const controllerChangeHandler = () => {
                        clearTimeout(timeout);
                        resolve(true);
                    };

                    const timeout = setTimeout(() => {
                        navigator.serviceWorker.removeEventListener('controllerchange', controllerChangeHandler);
                        resolve(false);
                    }, 5000);

                    navigator.serviceWorker.addEventListener('controllerchange', controllerChangeHandler, { once: true });
                });

                if (controllerChanged) {
                    // Only clear the deferred flag after confirming the update took effect
                    sessionStorage.removeItem('deferredSwUpdate');
                    console.log('ServiceWorkerUpdateController: Controller changed, reloading page');
                    window.location.reload();
                    return true;
                } else {
                    // Don't clear flag - controller didn't actually change, retry next time
                    console.warn('ServiceWorkerUpdateController: Timeout waiting for controller change - will retry later');
                    return false;
                }
            } else {
                console.log('ServiceWorkerUpdateController: No waiting worker found (may have already activated)');
                sessionStorage.removeItem('deferredSwUpdate');
                return false;
            }
        } catch (error) {
            console.error('ServiceWorkerUpdateController: Error applying deferred update:', error);
            // Don't clear flag on error - retry next time
            return false;
        }
    }

    /**
     * Check if an exam is currently in progress.
     * @returns {boolean}
     */
    isExamInProgress() {
        return sessionStorage.getItem('examInProgress') === 'true';
    }

    /**
     * Check if there's a deferred update waiting to be applied.
     * @returns {boolean}
     */
    hasDeferredUpdate() {
        return sessionStorage.getItem('deferredSwUpdate') === 'true';
    }

    /**
     * Clear any deferred update flag without applying it.
     * Used when the waiting worker is no longer available.
     */
    clearDeferredUpdate() {
        console.log('ServiceWorkerUpdateController: Clearing deferred update flag');
        sessionStorage.removeItem('deferredSwUpdate');
    }
}
